import { useRef, useState, type FormEvent, type ReactNode } from 'react'

import { useStepper } from '@/components/Stepper/StepperContext'

import { currencyToNumber, formatCurrency } from '@/core/utils/currency'

import { FgtsLoanSimulatorContext } from '../context/FgtsLoanSimulatorContext'
import { simulateFgtsLoan } from '../simulator/simulateFgtsLoan'
import { SIMULATION_FORM_STEP, SIMULATION_RESULT_STEP } from '../utils'

export const FgtsLoanSimulatorProvider = ({
  ctaHref,
  children,
}: {
  ctaHref: string
  children?: ReactNode
}) => {
  const { goToStep } = useStepper()
  const [hasFgts, setHasFgts] = useState<boolean>(true)
  const birthDateRef = useRef<HTMLInputElement>(null)
  const availableAmountRef = useRef<HTMLInputElement>(null)
  const salaryRef = useRef<HTMLInputElement>(null)
  const monthsWorkedRef = useRef<HTMLInputElement>(null)

  const backStep = () => {
    setHasFgts(true)
    goToStep(SIMULATION_FORM_STEP)
  }

  const onToggleIncomeType = () => setHasFgts(!hasFgts)

  const handleOnSubmit = (event: FormEvent) => {
    event.preventDefault()
    goToStep(SIMULATION_RESULT_STEP)
  }

  const calculateFromSalary = () => {
    const isValidData = birthDateRef.current && salaryRef.current && monthsWorkedRef.current
    if (!isValidData) return '0'

    return simulateFgtsLoan({
      birthDate: birthDateRef.current.value,
      months: Number(monthsWorkedRef.current.value),
      amount: currencyToNumber(salaryRef.current.value),
    })
  }

  const calculateFromFgts = () => {
    const isValidData = birthDateRef.current && availableAmountRef.current
    if (!isValidData) return '0'

    return simulateFgtsLoan({
      birthDate: birthDateRef.current.value,
      amount: currencyToNumber(availableAmountRef.current.value),
    })
  }

  const calculateAmountReceivable = () => (hasFgts ? calculateFromFgts() : calculateFromSalary())

  const amountReceivable = formatCurrency(calculateAmountReceivable())

  return (
    <FgtsLoanSimulatorContext.Provider
      value={{
        birthDateRef,
        availableAmountRef,
        salaryRef,
        monthsWorkedRef,
        hasFgts,
        ctaHref,
        backStep,
        amountReceivable,
        onToggleIncomeType,
        handleOnSubmit,
      }}
    >
      {children}
    </FgtsLoanSimulatorContext.Provider>
  )
}
